/**
 * Tube / CTL theoretical weight (v1 production formula).
 *
 * Round:  W_tons = (OD − t) × t × 0.0246615 × L_mm / 1e6 × qty
 * Square: W_tons = (side − t) × t × 0.0314 × L_mm / 1e6 × qty
 *         (0.0314 ≈ 4 × 0.00785 for hollow square section kg/m)
 */

export const STEEL_ROUND_K = 0.0246615;
export const STEEL_SQUARE_K = 0.0314;

export type TubeWeightDims = {
  shape?: string | null;
  /** Outer diameter mm (round) */
  od?: number | null;
  /** Side / major width mm (square / rectangle) */
  width?: number | null;
  thickness?: number | null;
  /** Length mm */
  length?: number | null;
  qty?: number | null;
};

function n(v: unknown): number {
  if (v === "" || v == null) return 0;
  const x = typeof v === "number" ? v : Number(v);
  return Number.isFinite(x) ? x : 0;
}

/** Coerce supabase numeric / form values to a finite number or null */
export function numOrNull(v: unknown): number | null {
  if (v === "" || v == null) return null;
  const x = typeof v === "number" ? v : Number(v);
  return Number.isFinite(x) ? x : null;
}

/** Weight of one piece in tons (length in mm). */
export function singlePieceWeightTons(dims: TubeWeightDims): number | null {
  const t = n(dims.thickness);
  const len = n(dims.length);
  if (!(t > 0) || !(len > 0)) return null;

  const shape = (dims.shape ?? "round").toLowerCase();
  const od = n(dims.od);
  const side = n(dims.width);

  if (shape === "round" || shape === "oval" || shape === "ellipse" || shape === "capsule") {
    if (!(od > t)) return null;
    return ((od - t) * t * STEEL_ROUND_K * len) / 1_000_000;
  }

  if (shape === "square" || shape === "rectangle" || shape === "custom") {
    const a = side > 0 ? side : od;
    if (!(a > t)) return null;
    return ((a - t) * t * STEEL_SQUARE_K * len) / 1_000_000;
  }

  if (od > t) {
    return ((od - t) * t * STEEL_ROUND_K * len) / 1_000_000;
  }
  return null;
}

export function totalWeightFromQty(dims: TubeWeightDims): number | null {
  const single = singlePieceWeightTons(dims);
  const qty = n(dims.qty);
  if (single == null || !(qty > 0)) return null;
  return Math.round(single * qty * 10000) / 10000;
}

export function qtyFromTotalWeight(dims: TubeWeightDims, totalWeightTons: number): number | null {
  const single = singlePieceWeightTons(dims);
  if (single == null || !(single > 0) || !(totalWeightTons > 0)) return null;
  // Qty is always whole pieces
  return Math.max(0, Math.round(totalWeightTons / single));
}
